import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
// ── EAGER routes ────────────────────────────────────────────────────
// Feb-2026 (Bulk Speed Pass): only HomePage + AuthCallback stay eager.
// HomePage is the initial paint; AuthCallback fires synchronously from a
// URL-hash check before Suspense mounts. Every other route is code-split
// so the initial JS bundle stays as small as possible on cellular cold
// starts.
import HomePage from "./pages/HomePage";
import AuthCallback from "./pages/AuthCallback";
// ── LAZY routes ─────────────────────────────────────────────────────
// Feb-2026 (user "speed pass"): every non-critical page is code-split
// so the initial JS bundle is ~70 % smaller. Route chunks load only
// when a user actually navigates to that page.
const PlayerPage = React.lazy(() => import("./pages/PlayerPage"));
const SpeakerPage = React.lazy(() => import("./pages/SpeakerPage"));
const LibraryPage = React.lazy(() => import("./pages/LibraryPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const UploadRecordingPage = React.lazy(() => import("./pages/UploadRecordingPage"));
const CommunityUploadPage = React.lazy(() => import("./pages/CommunityUploadPage"));
const LoadingScreensPreviewPage = React.lazy(() => import("./pages/LoadingScreensPreviewPage"));
const BootLabV2Page = React.lazy(() => import("./pages/BootLabV2Page"));
const KenBurnsLabPage = React.lazy(() => import("./pages/KenBurnsLabPage"));
const LogoLabPage = React.lazy(() => import("./pages/LogoLabPage"));
const VaadSizeLabPage = React.lazy(() => import("./pages/VaadSizeLabPage"));
const VaadOtzarCssLabPage = React.lazy(() => import("./pages/VaadOtzarCssLabPage"));
const IconsNoRingPreviewPage = React.lazy(() => import("./pages/IconsNoRingPreviewPage"));
const TrendingPage = React.lazy(() => import("./pages/TrendingPage"));
const PlaylistsPage = React.lazy(() => import("./pages/PlaylistsPage"));
const PlaylistDetailPage = React.lazy(() => import("./pages/PlaylistDetailPage"));
const PortraitCropAdminPage = React.lazy(() => import("./pages/PortraitCropAdminPage"));
const AdminFolderManagerPage = React.lazy(() => import("./pages/AdminFolderManagerPage"));
import OfflineBanner from "./components/OfflineBanner";
import BootSplash from "./components/BootSplash";
import BulkDownloadCancelPill from "./components/BulkDownloadCancelPill";
const WatchlistPage = React.lazy(() => import("./pages/WatchlistPage"));
const NewFeaturesPage = React.lazy(() => import("./pages/NewFeaturesPage"));
const AdminPanelPage = React.lazy(() => import("./pages/AdminPanelPage"));
const AdminArchivesPage = React.lazy(() => import("./pages/AdminArchivesPage"));
const NewVaadArchivePage = React.lazy(() => import("./pages/NewVaadArchivePage"));
const AdminMediaArchivesPage = React.lazy(() => import("./pages/AdminMediaArchivesPage"));
const AdminPlaybackDiagnosticsPage = React.lazy(() => import("./pages/AdminPlaybackDiagnosticsPage"));
const CCNetworkPage = React.lazy(() => import("./pages/CCNetworkPage"));
const CCFamilyTreePage = React.lazy(() => import("./pages/CCFamilyTreePage"));
const CCFamilyTreeRulesPage = React.lazy(() => import("./pages/CCFamilyTreeRulesPage"));
const AdminRecordingSearchPage = React.lazy(() => import("./pages/AdminRecordingSearchPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const AccountPage = React.lazy(() => import("./pages/AccountPage"));
const DonatePage = React.lazy(() => import("./pages/DonatePage"));
const DonateThankYouPage = React.lazy(() => import("./pages/DonateThankYouPage"));
const CCPictureLibraryPage = React.lazy(() => import("./pages/CCPictureLibraryPage"));
const VideosPage = React.lazy(() => import("./pages/VideosPage"));
const FavoritesPage = React.lazy(() => import("./pages/FavoritesPage"));
const CCPublicationsPage = React.lazy(() => import("./pages/CCPublicationsPage"));
const ByTheParshaPage = React.lazy(() => import("./pages/ByTheParshaPage"));
const ByTopicPage = React.lazy(() => import("./pages/ByTopicPage"));
const UpcomingPage = React.lazy(() => import("./pages/UpcomingPage"));
const HistoryPage = React.lazy(() => import("./pages/HistoryPage"));
const DownloadsPage = React.lazy(() => import("./pages/DownloadsPage"));
const TheBranchesPage = React.lazy(() => import("./pages/TheBranchesPage"));
const AudioConverterPage = React.lazy(() => import("./pages/AudioConverterPage"));
const LaunchAssetsPage = React.lazy(() => import("./pages/LaunchAssetsPage"));
const LibraryExportPage = React.lazy(() => import("./pages/LibraryExportPage"));
const SpeakersPage = React.lazy(() => import("./pages/SpeakersPage"));
const MorePage = React.lazy(() => import("./pages/MorePage"));
const TranscriptsPage = React.lazy(() => import("./pages/TranscriptsPage"));
const MyQuotesPage = React.lazy(() => import("./pages/MyQuotesPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const DownloadInstructionsPage = React.lazy(() => import("./pages/DownloadInstructionsPage"));
const BackdropPreviewPage = React.lazy(() => import("./pages/BackdropPreviewPage"));
const PalettePreviewPage = React.lazy(() => import("./pages/PalettePreviewPage"));
const ScenePreviewPage = React.lazy(() => import("./pages/ScenePreviewPage"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = React.lazy(() => import("./pages/TermsPage"));
import { PlayerProvider } from "./contexts/PlayerContext";
import { SearchProvider } from "./contexts/SearchContext";
import { MiniPlayer } from "./components/player/MiniPlayer";import CanvasCartPill from "./components/CanvasCartPill";
import ErrorBoundary from "./components/ErrorBoundary";
import { useMobileTabSwipe } from "./hooks/useMobileTabSwipe";
import HaskamaButton from "./components/HaskamaButton";
import SimpleModeBootstrap from "./components/SimpleModeBootstrap";
// IosInstallPrompt was removed per user request (Feb-2026) — see the
// note where it used to render in the JSX tree.
import { initReadPrefs } from "./utils/localSettings";
import { Toaster, toast } from "./components/ui/sonner";
import useIsAdmin from "./hooks/useIsAdmin";
import AdminTextEditor from "./components/AdminTextEditor";
import AdminGuard from "./components/AdminGuard";
import { migrateLocalToServer, PORTRAIT_ZOOM_SAVE_EVENT } from "./utils/speakerZoomOverrides";
import { SHOW_AUTH, SHOW_DONATE, SHOW_UPLOAD, SHOW_CANVAS, SHOW_ADMIN, SHOW_CC_PAGES } from "./config/flags";
import { AccessProvider } from "./contexts/AccessContext";
import AccessGateModal from "./components/access/AccessGateModal";
import { FullTierRoute } from "./components/access/LockedNotice";

/** Runs once per admin session: pushes any portrait-framing adjustments still
 *  living in this browser's localStorage up to the server so every device sees
 *  them. Non-admins get silently no-op'd because the PUT will 401 and the
 *  migration flag won't be set. Shows a small toast when adjustments are
 *  actually pushed. */
function AdminFramingSync() {
  const isAdmin = useIsAdmin();
  React.useEffect(() => {
    if (!isAdmin) return;
    migrateLocalToServer().then((r) => {
      if (r && r.pushed && r.pushed > 0) {
        try {
          toast.success(`Synced ${r.pushed} portrait framing edits to the server`);
        } catch { /* ignore */ }
      }
    });
  }, [isAdmin]);

  // Watch every save-attempt so admins get real feedback if a PUT is refused.
  // If the access_token expired we auto-refresh in speakerZoomOverrides.js;
  // only when the *refresh_token* is also gone (>=7 days idle or logged out)
  // do we surface a "session expired — log in again" toast with a login CTA.
  React.useEffect(() => {
    let lastFail = 0;
    let lastOk = 0;
    let lastExpiredToast = 0;
    const onSave = (e) => {
      const { ok, status, sessionExpired: expired } = e.detail || {};
      const now = Date.now();
      if (ok) {
        if (now - lastOk < 1200) return;
        lastOk = now;
        // Silent on success to avoid toast spam during dragging.
      } else if (expired) {
        // Debounce: one login prompt per 15s max.
        if (now - lastExpiredToast < 15000) return;
        lastExpiredToast = now;
        try {
          toast.error("Session expired — please log in again to save framing", {
            action: {
              label: "Log in",
              onClick: () => { window.location.href = "/login"; },
            },
            duration: 10000,
          });
        } catch { /* ignore */ }
      } else {
        if (now - lastFail < 3000) return;
        lastFail = now;
        try {
          if (status === 401 || status === 403) {
            toast.error("Sign in as admin to save portrait framing across devices");
          } else {
            toast.error(`Couldn't save framing (HTTP ${status || 'offline'})`);
          }
        } catch { /* ignore */ }
      }
    };
    window.addEventListener(PORTRAIT_ZOOM_SAVE_EVENT, onSave);
    return () => window.removeEventListener(PORTRAIT_ZOOM_SAVE_EVENT, onSave);
  }, []);

  return null;
}

function AppRouter() {
  const location = useLocation();
  useMobileTabSwipe();

  // Feb 2026 (user "make the images actually show up in preview"):
  // preload the curated cover pool as soon as the app mounts so the
  // FIRST paint of any RecordingCard / folder icon already has the
  // right pool image ready. Without this the first render shows the
  // dark placeholder for ~200ms until /api/speaker-cover-pool returns.
  React.useEffect(() => {
    import('./hooks/useCoverPool').then((mod) => {
      if (typeof mod.warmCoverPool === 'function') mod.warmCoverPool();
    }).catch(() => {});
  }, []);

  // CRITICAL: Check for session_id synchronously during render to prevent race conditions
  // This must happen BEFORE React Router processes the route
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <React.Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="route-suspense-fallback">
        <img
          src="/vaad-otzar-logo.jpg"
          alt="The Vaad Otzar"
          className="w-[54vmin] max-w-[360px] aspect-square rounded-3xl object-cover"
          style={{ boxShadow: '0 30px 80px #c9a43744, 0 0 0 2px #c9a43788', animation: 'suspenseLogoPulse 1400ms ease-in-out infinite alternate' }}
        />
        <style>{`
          @keyframes suspenseLogoPulse {
            from { opacity: 0.72; transform: scale(0.96); }
            to   { opacity: 1;    transform: scale(1.02); }
          }
        `}</style>
      </div>
    }>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/speakers" element={<SpeakersPage />} />
      {SHOW_AUTH && <Route path="/login" element={<LoginPage />} />}
      {SHOW_AUTH && <Route path="/register" element={<RegisterPage />} />}
      <Route path="/player/:id" element={<PlayerPage />} />
      <Route path="/library" element={<LibraryPage />} />
      <Route path="/speaker/:speakerName" element={<SpeakerPage />} />
      {SHOW_UPLOAD && <Route path="/upload" element={<AdminGuard><CommunityUploadPage /></AdminGuard>} />}
      {SHOW_ADMIN && <Route path="/admin/upload" element={<AdminGuard><UploadRecordingPage /></AdminGuard>} />}
      <Route path="/loading-preview" element={<LoadingScreensPreviewPage />} />
      <Route path="/boot-lab-v2" element={<BootLabV2Page />} />
      <Route path="/loading-preview/kenburns" element={<KenBurnsLabPage />} />
      <Route path="/logo-lab" element={<LogoLabPage />} />
      <Route path="/logo-lab/vaad-size" element={<VaadSizeLabPage />} />
      <Route path="/logo-lab/css-text" element={<VaadOtzarCssLabPage />} />
      <Route path="/icons-no-ring" element={<IconsNoRingPreviewPage />} />
      <Route path="/trending" element={<TrendingPage />} />
      <Route path="/playlists" element={<PlaylistsPage />} />
      <Route path="/playlists/:id" element={<PlaylistDetailPage />} />
      {SHOW_ADMIN && <Route path="/admin/portrait-crop" element={<AdminGuard><PortraitCropAdminPage /></AdminGuard>} />}
      {SHOW_ADMIN && <Route path="/admin/folders" element={<AdminGuard><AdminFolderManagerPage /></AdminGuard>} />}
      <Route path="/watchlist" element={<WatchlistPage />} />
      <Route path="/favorites" element={<FavoritesPage />} />
      <Route path="/new-features" element={<NewFeaturesPage />} />
      {SHOW_ADMIN && <Route path="/admin" element={<AdminGuard><AdminPanelPage /></AdminGuard>} />}
      {SHOW_ADMIN && <Route path="/admin/archives" element={<AdminGuard><AdminArchivesPage /></AdminGuard>} />}
      {SHOW_ADMIN && <Route path="/admin/new-vaad-archive" element={<NewVaadArchivePage />} />}
      {SHOW_ADMIN && <Route path="/admin/media-archive" element={<AdminGuard><AdminMediaArchivesPage /></AdminGuard>} />}
      {SHOW_ADMIN && <Route path="/admin/playback-diagnostics" element={<AdminGuard><AdminPlaybackDiagnosticsPage /></AdminGuard>} />}
      {SHOW_ADMIN && <Route path="/admin/recording-search" element={<AdminGuard><AdminRecordingSearchPage /></AdminGuard>} />}
      {!SHOW_ADMIN && <Route path="/admin" element={<Navigate to="/" replace />} />}
      {!SHOW_ADMIN && <Route path="/admin/*" element={<Navigate to="/" replace />} />}
      {SHOW_CC_PAGES && <Route path="/cc-network" element={<CCNetworkPage />} />}
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      {SHOW_AUTH && <Route path="/account" element={<AccountPage />} />}
      {SHOW_DONATE && <Route path="/donate" element={<DonatePage />} />}
      {SHOW_DONATE && <Route path="/donate/thank-you" element={<DonateThankYouPage />} />}
      <Route path="/cc-picture" element={<CCPictureLibraryPage />} />
      {SHOW_CC_PAGES && <Route path="/cc-family-tree" element={<FullTierRoute title="Continue with your email to view this page" body="No password needed — just your email once."><CCFamilyTreePage /></FullTierRoute>} />}
      {SHOW_ADMIN && SHOW_CC_PAGES && <Route path="/cc-family-tree/rules" element={<CCFamilyTreeRulesPage />} />}
      <Route path="/library-export" element={<LibraryExportPage />} />
      <Route path="/videos" element={<VideosPage />} />
      <Route path="/cc-pdf" element={<Navigate to="/cc-publications" replace />} />
      <Route path="/cc-publications" element={<CCPublicationsPage />} />
      <Route path="/parsha" element={<ByTheParshaPage />} />
      <Route path="/parsha/:slug" element={<ByTheParshaPage />} />
      <Route path="/topics" element={<ByTopicPage />} />
      <Route path="/topic/:slug" element={<ByTopicPage />} />
      <Route path="/upcoming" element={<UpcomingPage />} />      <Route path="/history" element={<HistoryPage />} />
      <Route path="/downloads" element={<DownloadsPage />} />      <Route path="/branches" element={<TheBranchesPage />} />
      <Route path="/audio-converter" element={<AudioConverterPage />} />
      <Route path="/launch" element={<LaunchAssetsPage />} />
      <Route path="/download-center" element={<DownloadInstructionsPage />} />
      <Route path="/more" element={<MorePage />} />
      <Route path="/transcripts" element={<TranscriptsPage />} />
      <Route path="/my-quotes" element={<MyQuotesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/backdrop-preview" element={<BackdropPreviewPage />} />
      <Route path="/palette-preview" element={<PalettePreviewPage />} />
      <Route path="/scene-preview" element={<ScenePreviewPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </React.Suspense>
  );
}

function NotFoundPage() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#ffffff] p-6 text-center">
      <h1 className="text-5xl font-bold text-[#0f1729] mb-2">404</h1>
      <p className="text-[#4b5563] mb-6">This page doesn&apos;t exist.</p>
      <a href="/" className="inline-block px-4 py-2 rounded-lg bg-[#9a7b24] hover:bg-[#7d6119] text-white font-semibold">
        Go home
      </a>
    </div>
  );
}

function HaskamaOnHomeOnly() {
  const location = useLocation();
  if (!SHOW_DONATE) return null;
  if (location.pathname !== '/') return null;
  return <HaskamaButton />;
}

function App() {
  // Boot-time: apply persisted read prefs (high-contrast body class).
  React.useEffect(() => { initReadPrefs(); }, []);
  // Feb-2026 (user "when i scroll down all seen is blue"): index.html paints
  // <body> and #root navy (#0f1729) so the cold-boot splash has no white
  // flash. But that navy also shows through every UNPAINTED scroll tile —
  // scroll faster than the compositor paints and the whole screen goes
  // blue. Once React mounts, BootSplash carries its own opaque navy
  // overlay, so we drop the inline navy here and let the themed
  // `body { background: var(--background) }` CSS (light cream / dark navy)
  // take over. Unpainted tiles now match the content color = invisible.
  React.useEffect(() => {
    document.body.style.removeProperty('background');
    const root = document.getElementById('root');
    if (root) root.style.removeProperty('background');
  }, []);
  // Feb-2026 (superfast pass): the previous "warm the boot-mosaic
  // cache once per app-mount" effect fetched `/api/boot-mosaic-pool`
  // and wrote it to `vaad_boot_pool_cache_v4` — a DEAD key that
  // BootSplash no longer reads (it reads v5). That was a wasted
  // network round-trip on every launch. Removed. BootSplash now
  // handles its own pool warming via `/boot-pool.json` (bundled) +
  // SW-cached `/api/boot-mosaic-pool` (0-network on 2nd+ visits).
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <PlayerProvider>
            <SearchProvider>
            <AccessProvider>
              <AppRouter />
              <AccessGateModal />
              <AdminFramingSync />
              <AdminTextEditor />
              <HaskamaOnHomeOnly />
              <MiniPlayer />
              {SHOW_CANVAS && <CanvasCartPill />}
              <OfflineBanner />
              <BulkDownloadCancelPill />
              <Toaster position="top-center" richColors closeButton />
              <BootSplash />
              <SimpleModeBootstrap />
            </AccessProvider>
              {/* Feb-2026 (user: "get rid of that pop-up that's
                  confusing and says to have audio not turn off share")
                  — removed IosInstallPrompt. Users who wrap the mobile
                  site via Median (native WebView) don't need PWA
                  install instructions; background audio works natively
                  through the wrapper's audio-mode config. */}
            </SearchProvider>
          </PlayerProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
